import React from "react";

import { Chip } from "@busbud/design-system-components/dist";
import AttendantMD from "@busbud/design-system-components/dist/Icons/Attendant/MD";
import CheckMD from "@busbud/design-system-components/dist/Icons/Check/MD";
import ClockMD from "@busbud/design-system-components/dist/Icons/Clock/MD";
import WifiMD from "@busbud/design-system-components/dist/Icons/Wifi/MD";
import Stack from "@busbud/design-system-components/dist/Stack";

import { useAppContext } from "@app/helpers/hooks";
import type { TranslatorService } from "@app/services/translator";

import {} from "./operator-ratings.connect";
import type {
  OperatorRatingsCategory,
  TripOperatorRatingCategory
} from "../helpers/operator-ratings";

import { useStyles } from "./operator-ratings.styles";

const CATEGORY_ICONS: Record<OperatorRatingsCategory, React.ReactNode> = {
  staff_rating: <AttendantMD />,
  timeliness_rating: <ClockMD />,
  cleanliness_rating: <CheckMD />,
  wifi_rating: <WifiMD />
};

const RED_RATING_TRESHOLD = 0.2; // `rating` <= RED_RATING_TRESHOLD --> red chip
const YELLOW_RATING_TRESHOLD = 0.7; // `rating` <= YELLOW_RATING_TRESHOLD --> yellow chip

export const OperatorRatingCategories = ({
  categories
}: {
  categories: TripOperatorRatingCategory[];
}) => {
  const { classes } = useStyles();
  const { translator } = useAppContext();

  return (
    <Stack flexWrap="wrap" className={classes.ratingCategories}>
      {categories.map(category => {
        if (!category.rating) {
          return null;
        }
        const rating_percentage = Math.round(category.rating * 100);
        const chip_label_and_title = translateRatingElement(
          translator,
          category.id,
          rating_percentage
        );

        return (
          <Chip
            key={category.id}
            variant="outlined"
            color="neutral"
            size="lg"
            startIcon={CATEGORY_ICONS[category.id]}
            endIcon={
              <Chip
                variant="subtle"
                color={getCategoryColor(category.rating)}
                size="sm"
                label={`${rating_percentage}%`}
                className={classes.ratingCategoryChipEndIcon}
              />
            }
            {...chip_label_and_title}
          />
        );
      })}
    </Stack>
  );
};

function getCategoryColor(rating: number) {
  if (rating <= RED_RATING_TRESHOLD) {
    return "red";
  } else if (rating <= YELLOW_RATING_TRESHOLD) {
    return "yellow";
  } else {
    return "green";
  }
}

function translateRatingElement(
  translator: TranslatorService,
  category_id: OperatorRatingsCategory,
  percentage: number
): { label: string; title: string } {
  switch (category_id) {
    case "staff_rating": {
      return {
        label: translator.t("!partner.reviews.staff_rating"),
        title: translator.t("!partner.reviews.staff_rating_title", {
          percentage
        })
      };
    }
    case "timeliness_rating":
      return {
        label: translator.t("!partner.reviews.timeliness_rating"),
        title: translator.t("!partner.reviews.timeliness_rating_title", {
          percentage
        })
      };
    case "cleanliness_rating": {
      return {
        label: translator.t("!partner.reviews.cleanliness_rating"),
        title: translator.t("!partner.reviews.cleanliness_rating_title", {
          percentage
        })
      };
    }
    case "wifi_rating": {
      return {
        label: translator.t("!partner.reviews.wifi_rating"),
        title: translator.t("!partner.reviews.wifi_rating_title", {
          percentage
        })
      };
    }
  }
}
